/**
 * Treasure Trails storefront hooks for OptiMonk.
 *
 * ============================================================================
 * PART 1 - the `trail_added_to_basket` event
 * ============================================================================
 *
 * Dispatched on `document` (bubbling, so a window listener catches it too) after a
 * TRAIL product has been successfully added to the basket. Not on click - it waits for
 * Shopify to confirm the add.
 *
 *   document.addEventListener('trail_added_to_basket', (e) => console.log(e.detail));
 *
 * `detail` carries productHandle, productId, productTitle, variantId, addedItem,
 * itemCount and cartTotal.
 *
 * A product counts as a trail when it belongs to a collection with the handle `trails`.
 * That test happens in Liquid, in snippets/tt-trail-product-attributes.liquid, which
 * stamps `data-tt-trail` onto every <product-form>. It has to be done there because
 * collection membership is not exposed to storefront JS.
 *
 * Use it as a JavaScript event trigger on an OptiMonk campaign.
 *
 * ============================================================================
 * PART 2 - sticky bar page switching
 * ============================================================================
 *
 * ONE OptiMonk campaign holds several pages. Each page declares the cart condition it
 * should appear under, using a marker element in that page's Custom HTML element. This
 * file picks the page whose condition matches the current cart and shows it, live, as
 * the basket changes.
 *
 * ---------------------------------------------------------------------------
 * MARKER SYNTAX
 * ---------------------------------------------------------------------------
 *
 *   <span data-ttsb-items-in-cart="1" data-ttsb-rule="equals"></span>
 *
 * The element is never displayed. Put one in each page's Custom HTML.
 *
 * SUBJECTS - what is being compared
 *
 *   data-ttsb-items-in-cart   total quantity of items in the basket
 *   data-ttsb-cart-total      basket total in PENCE, so £30.00 is 3000
 *   data-ttsb-line-count      number of distinct products, ignoring quantity
 *
 * RULES - how it is compared. Set with data-ttsb-rule, defaults to `equals`.
 *
 *   equals            exactly this number
 *   not-equals        anything but this number
 *   at-least          this number or more
 *   at-most           this number or fewer
 *   between           this number up to data-ttsb-max, inclusive
 *
 * These aliases all work, so the wording can read however suits:
 *   equals-or-above / equal-or-above / greater-or-equal / gte  ->  at-least
 *   equals-or-below / equal-or-below / less-or-equal / lte     ->  at-most
 *   is                                                          ->  equals
 *   is-not                                                      ->  not-equals
 *
 * MODIFIERS
 *
 *   data-ttsb-max        upper bound, only used by the `between` rule
 *   data-ttsb-priority   higher wins when several pages match. Defaults to 0, and
 *                        ties fall back to page order.
 *   data-ttsb-default    marks the fallback page, shown when nothing else matches.
 *                        Takes no value.
 *   data-ttsb-apply-coupon
 *                        when this page becomes active, read the code OptiMonk rendered
 *                        into .om-coupon-text and write it to the cart. Takes no value.
 *                        Looks on the active page first, then anywhere in the campaign,
 *                        since the coupon is often designed onto a different page from
 *                        the one that qualifies. Works with third-party codes because we
 *                        read what is rendered rather than needing to know it up front.
 *                        Removed again when the basket moves to a page that does not opt
 *                        in. Only ever removes the code WE applied - a voucher the
 *                        customer typed is left alone.
 *
 *                        Note: we do NOT click their coupon control. That only copies to
 *                        the clipboard, does not redeem, and triggers a browser
 *                        clipboard permission prompt.
 *
 * ---------------------------------------------------------------------------
 * EXAMPLES
 * ---------------------------------------------------------------------------
 *
 * A four page bar driven by basket quantity:
 *
 *   Page 1   <span data-ttsb-default></span>
 *   Page 2   <span data-ttsb-items-in-cart="1" data-ttsb-rule="equals"></span>
 *   Page 3   <span data-ttsb-items-in-cart="2" data-ttsb-rule="equals"></span>
 *   Page 4   <span data-ttsb-items-in-cart="3" data-ttsb-rule="at-least"></span>
 *
 * A basket value threshold, £50 or more:
 *
 *   <span data-ttsb-cart-total="5000" data-ttsb-rule="at-least"></span>
 *
 * A range, 2 to 4 items:
 *
 *   <span data-ttsb-items-in-cart="2" data-ttsb-max="4" data-ttsb-rule="between"></span>
 *
 * Several conditions on one page are ANDed, so this needs BOTH 2+ items and £30+:
 *
 *   <span data-ttsb-items-in-cart="2" data-ttsb-cart-total="3000" data-ttsb-rule="at-least"></span>
 *
 * Overlapping rules resolved by priority - this page wins over a plain `at-least 3`:
 *
 *   <span data-ttsb-items-in-cart="5" data-ttsb-rule="at-least" data-ttsb-priority="10"></span>
 *
 * ---------------------------------------------------------------------------
 * WHEN IT RE-EVALUATES
 * ---------------------------------------------------------------------------
 *
 * On page load, on any Dawn cart update (add, remove, quantity change), on
 * `trail_added_to_basket`, when the tab regains focus, and whenever OptiMonk injects or
 * changes campaign markup. No polling.
 *
 * ---------------------------------------------------------------------------
 * WORTH KNOWING
 * ---------------------------------------------------------------------------
 *
 * - OptiMonk renders Custom HTML inside an about:blank iframe, so the markers are not
 *   in the main document. This file reads into those frames. It is same origin, so that
 *   works, but it is why a plain document.querySelector for a marker finds nothing.
 * - Pages are identified by `data-om-step`, which OptiMonk sets. The teaser is not a
 *   page and is correctly ignored.
 * - Nothing is hardcoded to a campaign or page id. Pages are resolved by walking up
 *   from each marker, so renumbering a campaign does not break anything.
 * - `items-in-cart` counts EVERYTHING in the basket, not just trails. A gift shop item
 *   will advance the bar. Trail-only counting would need a signal the cart JSON does
 *   not currently carry.
 * - If no page matches and there is no default, the bar is left exactly as OptiMonk
 *   rendered it.
 *
 * ---------------------------------------------------------------------------
 * DIAGNOSTICS
 * ---------------------------------------------------------------------------
 *
 *   ttsbDebug()      table of every marker, its rule, whether it currently matches,
 *                    and the page it resolved to, plus the cart state in use
 *   ttsbEvaluate()   force a re-read of the cart and a re-evaluation
 *
 * To extend the vocabulary, add an entry to TTSB_SUBJECTS or TTSB_RULES below.
 */

(function () {
  /* ======================================================================== *
   * 1. trail_added_to_basket
   * ======================================================================== */

  const TT_TRAIL_EVENT_NAME = 'trail_added_to_basket';
  const TT_LOG_PREFIX = '[tt-sticky-bar]';

  let pendingTrail = null;

  // Capture phase, so it runs before Dawn's own handler calls preventDefault().
  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;

      const productForm = form.closest('product-form');
      if (!productForm) return;

      // Reset every time, so a non-trail add cannot inherit a previous flag.
      pendingTrail =
        productForm.dataset.ttTrail === 'true'
          ? {
              productHandle: productForm.dataset.ttProductHandle || null,
              productId: productForm.dataset.ttProductId || null,
              productTitle: productForm.dataset.ttProductTitle || null,
            }
          : null;
    },
    true
  );

  /* ======================================================================== *
   * 2. Sticky bar page switching
   * ======================================================================== */

  const TTSB_MARKER_SELECTOR = '[data-ttsb-rule], [data-ttsb-default]';

  // Pages carry data-om-step. The teaser is also a .om-canvas but has no step, so
  // keying off the step attribute selects the real pages and excludes the teaser.
  const TTSB_PAGE_SELECTOR = '[data-om-step]';

  // OptiMonk renders custom HTML inside an about:blank iframe, so markers are NOT in
  // the parent document. Same origin, so we can read in - we just have to look.
  const TTSB_CUSTOM_HTML_FRAME = '.om-custom-html iframe';

  // Each popup or sticky bar sits in its own .om-holder. That is the boundary we group
  // markers by and scope page hiding to, so campaigns never interfere with each other.
  const TTSB_CAMPAIGN_SELECTOR = '.om-holder, [id^="om-campaign-"]';

  // Marker -> the iframe element in the parent document that contains it.
  const ttsbMarkerFrames = new WeakMap();

  /**
   * Rule vocabulary. Aliases exist so the wording in the OptiMonk editor can be
   * whatever reads best without breaking anything.
   */
  const TTSB_RULES = {
    equals: (actual, value) => actual === value,
    'not-equals': (actual, value) => actual !== value,
    'at-least': (actual, value) => actual >= value,
    'at-most': (actual, value) => actual <= value,
    between: (actual, value, max) => actual >= value && actual <= max,
  };

  const TTSB_RULE_ALIASES = {
    'equals-or-above': 'at-least',
    'equal-or-above': 'at-least',
    'greater-or-equal': 'at-least',
    gte: 'at-least',
    'equals-or-below': 'at-most',
    'equal-or-below': 'at-most',
    'less-or-equal': 'at-most',
    lte: 'at-most',
    is: 'equals',
    'is-not': 'not-equals',
  };

  /** Subjects a marker can test. Add here to extend the vocabulary. */
  const TTSB_SUBJECTS = {
    ttsbItemsInCart: (state) => state.itemCount,
    ttsbCartTotal: (state) => state.totalPrice,
    ttsbLineCount: (state) => state.lineCount,
  };


  let ttsbState = { itemCount: 0, totalPrice: 0, lineCount: 0 };
  let ttsbObserver = null;
  /**
   * Per-campaign state.
   *
   * There can be several campaigns on a page - a sticky bar and one or more popups -
   * each in its own .om-holder. They must be evaluated INDEPENDENTLY: one campaign's
   * winning page must never hide another's, and a coupon applied by one must never be
   * removed by another.
   */
  const ttsbCampaignState = new WeakMap();

  function stateFor(campaign) {
    if (!ttsbCampaignState.has(campaign)) {
      ttsbCampaignState.set(campaign, { activePage: null, appliedCode: null, couponObserver: null });
    }
    return ttsbCampaignState.get(campaign);
  }
  let ttsbApplying = false;
  let ttsbEvaluateTimer = null;

  /**
   * Debounced, and ignored while we are mid-swap so our own class changes cannot
   * retrigger the observer that called us.
   */
  function scheduleEvaluate(reason) {
    if (ttsbApplying) return;
    window.clearTimeout(ttsbEvaluateTimer);
    ttsbEvaluateTimer = window.setTimeout(() => evaluate(reason), 100);
  }

  function normaliseRule(rule) {
    const value = (rule || 'equals').trim().toLowerCase();
    return TTSB_RULE_ALIASES[value] || value;
  }

  /**
   * Markers live inside the custom-HTML iframes, so collect from there as well as the
   * parent document. Access is wrapped because a cross-origin frame would throw.
   */
  function findMarkers() {
    const markers = [...document.querySelectorAll(TTSB_MARKER_SELECTOR)];

    document.querySelectorAll(TTSB_CUSTOM_HTML_FRAME).forEach((frame) => {
      let doc = null;

      try {
        doc = frame.contentDocument;
      } catch (error) {
        return;
      }

      if (!doc) return;

      doc.querySelectorAll(TTSB_MARKER_SELECTOR).forEach((marker) => {
        ttsbMarkerFrames.set(marker, frame);
        markers.push(marker);
      });
    });

    return markers;
  }

  /**
   * The page holding a marker. For markers inside an iframe we resolve from the iframe
   * element in the parent document, since closest() cannot cross the boundary.
   */
  function pageFor(marker) {
    const frame = ttsbMarkerFrames.get(marker);
    return (frame || marker).closest(TTSB_PAGE_SELECTOR);
  }

  /**
   * The campaign a marker belongs to. Each popup or sticky bar is wrapped in its own
   * .om-holder, which is the boundary we group and scope by. Resolved through the
   * iframe element for markers inside custom HTML, since closest() cannot cross it.
   */
  function campaignFor(marker) {
    const frame = ttsbMarkerFrames.get(marker);
    return (frame || marker).closest(TTSB_CAMPAIGN_SELECTOR);
  }

  /**
   * Is OptiMonk actually displaying this campaign?
   *
   * Campaigns that are not targeted at the current visitor or page are still rendered
   * into the DOM, but their overlay container is set to display:none with zero width
   * and height. We must skip those: driving pages inside a hidden campaign is pointless,
   * and worse, a hidden campaign carrying data-ttsb-apply-coupon would apply a discount
   * the customer never saw.
   */
  function isCampaignActive(campaign) {
    const overlay =
      campaign.querySelector('[id^="om-overlay-campaign-"]') || campaign.closest('[id^="om-overlay-campaign-"]');

    const target = overlay || campaign;
    return window.getComputedStyle(target).display !== 'none';
  }

  /** Markers grouped by the campaign that owns them. */
  function groupMarkersByCampaign(markers) {
    const groups = new Map();

    markers.forEach((marker) => {
      const campaign = campaignFor(marker);
      if (!campaign) return;
      if (!groups.has(campaign)) groups.set(campaign, []);
      groups.get(campaign).push(marker);
    });

    return groups;
  }

  /**
   * A marker matches when EVERY subject it declares satisfies the rule, so several
   * conditions can be ANDed on one page:
   *   <span data-ttsb-items-in-cart="2" data-ttsb-cart-total="3000" data-ttsb-rule="at-least"></span>
   */
  function markerMatches(marker, state) {
    if (marker.dataset.ttsbDefault !== undefined && marker.dataset.ttsbRule === undefined) return false;

    const rule = normaliseRule(marker.dataset.ttsbRule);
    const test = TTSB_RULES[rule];

    if (!test) {
      console.warn(`${TT_LOG_PREFIX} unknown rule "${marker.dataset.ttsbRule}" - ignoring this page`);
      return false;
    }

    const max = Number(marker.dataset.ttsbMax);
    const subjects = Object.keys(TTSB_SUBJECTS).filter((key) => marker.dataset[key] !== undefined);

    if (subjects.length === 0) return false;

    return subjects.every((key) => {
      const expected = Number(marker.dataset[key]);
      if (!Number.isFinite(expected)) return false;
      return test(TTSB_SUBJECTS[key](state), expected, max);
    });
  }

  /** Highest data-ttsb-priority wins; ties fall back to document order. */
  function chooseMarker(markers, state) {
    const matching = markers.filter((marker) => markerMatches(marker, state));

    if (matching.length > 0) {
      return matching.sort(
        (a, b) => (Number(b.dataset.ttsbPriority) || 0) - (Number(a.dataset.ttsbPriority) || 0)
      )[0];
    }

    return markers.find((marker) => marker.dataset.ttsbDefault !== undefined) || null;
  }

  /**
   * Mirrors exactly how OptiMonk shows and hides its own pages, rather than forcing it
   * with !important:
   *   shown:  class="om-canvas om-animated om-fadeIn actual"      style=""
   *   hidden: class="om-canvas om-canvas-hidden om-animated om-fadeOut" style="display:none"
   */
  function showPage(target, pages) {
    // Skip when everything is already correct. Now that we observe attribute changes,
    // rewriting classes and styles that are already right would generate mutations,
    // which would retrigger evaluation and loop.
    const alreadyCorrect = pages.every((page) =>
      page === target ? page.classList.contains('actual') : !page.classList.contains('actual')
    );

    if (alreadyCorrect) return;

    ttsbApplying = true;

    pages.forEach((page) => {
      if (page === target) {
        page.classList.remove('om-canvas-hidden', 'om-fadeOut');
        page.classList.add('om-fadeIn', 'actual');
        page.style.removeProperty('display');
      } else {
        page.classList.remove('actual', 'om-fadeIn');
        page.classList.add('om-canvas-hidden', 'om-fadeOut');
        page.style.display = 'none';
      }
    });

    window.setTimeout(() => {
      ttsbApplying = false;
    }, 0);
  }

  /**
   * Reads the code OptiMonk rendered and writes it to the cart ourselves.
   *
   * We previously clicked their coupon control, hoping their redemption path would run.
   * It does not: the click only copies to the clipboard, and it triggers a browser
   * clipboard permission prompt, which is not acceptable on a storefront.
   *
   * Reading the rendered code still works with third-party codes, because we take
   * whatever they put in .om-coupon-text rather than needing to know it in advance.
   */
  function couponCodeFor(page) {
    // Prefer a coupon on this page, but fall back to anywhere in the campaign. The
    // coupon is often designed onto the "you have unlocked it" page while the page that
    // actually qualifies is a later one, and every page is in the DOM regardless of
    // which is visible.
    const scope = page.closest('[id^="om-campaign-"]') || document;
    const text = page.querySelector('.om-coupon-text') || scope.querySelector('.om-coupon-text');

    if (!text) {
      console.warn(`${TT_LOG_PREFIX} page opted into a coupon but no coupon element was found in the campaign`);
      return null;
    }

    const holder = text.closest('[data-om-step]');
    if (holder && holder !== page) {
      console.debug(`${TT_LOG_PREFIX} using the coupon from page ${holder.dataset.omStep}`);
    }

    return text;
  }

  /** [[Coupon]] and similar tokens are OptiMonk placeholders, not real codes. */
  function isPlaceholder(code) {
    return !code || /\[\[.*\]\]|\{\{.*\}\}/.test(code);
  }

  /**
   * Applies the coupon for a page, waiting for it if it has not resolved yet.
   *
   * With CSV-uploaded unique codes OptiMonk renders a placeholder such as [[Coupon]]
   * and substitutes the real per-visitor code later. Their own copy button carries
   * "Discount code not available yet, please retry when the code is displayed", so the
   * code does reach the browser at some point - we just cannot assume it is there the
   * instant a page becomes active. So watch the element rather than reading it once.
   */
  function applyCouponFromPage(state, page) {
    stopWatchingCoupon(state);

    const element = couponCodeFor(page);
    if (!element) return;

    const code = (element.textContent || '').trim();

    if (!isPlaceholder(code)) {
      applyCouponCode(state, code);
      return;
    }

    console.log(`${TT_LOG_PREFIX} coupon is still a placeholder (${code || 'empty'}) - watching for it to resolve`);

    state.couponObserver = new MutationObserver(() => {
      const resolved = (element.textContent || '').trim();
      if (isPlaceholder(resolved)) return;

      stopWatchingCoupon(state);
      console.log(`${TT_LOG_PREFIX} coupon resolved to ${resolved}`);
      applyCouponCode(state, resolved);
    });

    state.couponObserver.observe(element, { childList: true, characterData: true, subtree: true });
  }

  function stopWatchingCoupon(state) {
    if (state.couponObserver) state.couponObserver.disconnect();
    state.couponObserver = null;
  }

  /**
   * Writes the code to the cart, preserving anything already applied - the discount
   * parameter replaces the whole set, so the full list has to be sent every time.
   */
  async function applyCouponCode(state, code) {
    if (!code || state.appliedCode === code) return;

    try {
      const current = await readCartCodes();
      if (current.some((existing) => existing.toLowerCase() === code.toLowerCase())) {
        state.appliedCode = code;
        return;
      }

      await writeCartCodes([...current, code]);
      state.appliedCode = code;
      console.log(`${TT_LOG_PREFIX} applied coupon ${code}`);
    } catch (error) {
      console.error(`${TT_LOG_PREFIX} could not apply coupon ${code}`, error);
    }
  }

  /**
   * Removes only the code THIS campaign applied. A voucher the customer typed, or a
   * code applied by a different campaign on the same page, is left alone.
   */
  async function removeAppliedCoupon(state) {
    if (!state.appliedCode) return;

    const code = state.appliedCode;
    state.appliedCode = null;

    try {
      const current = await readCartCodes();
      const next = current.filter((existing) => existing.toLowerCase() !== code.toLowerCase());
      if (next.length === current.length) return;

      await writeCartCodes(next);
      console.log(`${TT_LOG_PREFIX} removed coupon ${code} - the basket no longer qualifies`);
    } catch (error) {
      console.error(`${TT_LOG_PREFIX} could not remove coupon ${code}`, error);
    }
  }

  async function readCartCodes() {
    const response = await fetch(cartRoute('cart.js'), { cache: 'no-store', credentials: 'same-origin' });
    if (!response.ok) throw new Error(`cart.js failed with status ${response.status}`);
    const cart = await response.json();
    return (cart.discount_codes || []).filter((entry) => entry && entry.code).map((entry) => entry.code);
  }

  async function writeCartCodes(codes) {
    const response = await fetch(cartRoute('cart/update.js'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ discount: codes.join(',') }),
    });

    if (!response.ok) throw new Error(`cart/update.js failed with status ${response.status}`);
    return response.json();
  }

  function cartRoute(path) {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return `${root}${path}`.replace(/\/{2,}/g, '/');
  }

  /**
   * Evaluates EVERY campaign on the page, independently.
   *
   * Previously this swept all markers into one list, picked a single global winner, and
   * scoped the page hiding to whichever campaign happened to own the FIRST marker in
   * document order. With more than one marker-driven campaign present - a sticky bar
   * plus a popup - the winning page and the page list could come from different
   * campaigns, so the winner was never shown and the other campaign's pages were all
   * hidden. That is why the sticky bars stopped working once a second campaign went up.
   */
  function evaluate(reason) {
    const markers = findMarkers();
    if (markers.length === 0) return false;

    const groups = groupMarkersByCampaign(markers);

    if (groups.size === 0) {
      console.warn(`${TT_LOG_PREFIX} markers found but none inside an OptiMonk campaign wrapper`);
      return false;
    }

    let handled = false;

    groups.forEach((campaignMarkers, campaign) => {
      if (!isCampaignActive(campaign)) return;
      if (evaluateCampaign(campaign, campaignMarkers, reason)) handled = true;
    });

    return handled;
  }

  /** One campaign: choose its page from its own markers, hide only its own siblings. */
  function evaluateCampaign(campaign, markers, reason) {
    const pages = [...campaign.querySelectorAll(TTSB_PAGE_SELECTOR)];
    if (pages.length === 0) return false;

    const chosen = chooseMarker(markers, ttsbState);

    if (!chosen) {
      console.debug(
        `${TT_LOG_PREFIX} ${campaign.id || 'campaign'}: no page matches ${ttsbState.itemCount} item(s) and no default set`
      );
      return false;
    }

    const page = pageFor(chosen);

    // Guard against a marker resolving to a page outside this campaign, which is what
    // the old cross-campaign bug amounted to.
    if (!page || !pages.includes(page)) return false;

    showPage(page, pages);

    const state = stateFor(campaign);

    // Only on an actual page change, so a re-evaluate on the same page does not
    // re-apply the coupon.
    if (page !== state.activePage) {
      const optsIn = chosen.dataset.ttsbApplyCoupon !== undefined;
      console.log(
        `${TT_LOG_PREFIX} ${campaign.id || 'campaign'}: active page -> ${page.dataset.omStep} (${page.id}), applies coupon: ${optsIn}`
      );

      state.activePage = page;

      if (optsIn) {
        applyCouponFromPage(state, page);
      } else {
        stopWatchingCoupon(state);
        removeAppliedCoupon(state);
      }
    }

    console.debug(
      `${TT_LOG_PREFIX} ${campaign.id || 'campaign'}: showing page for ${describeMarker(chosen)} (${ttsbState.itemCount} items, reason: ${reason})`
    );

    return true;
  }

  function describeMarker(marker) {
    const subject = Object.keys(TTSB_SUBJECTS).find((key) => marker.dataset[key] !== undefined);
    if (!subject) return 'default';
    const label = subject.replace('ttsb', '').replace(/([A-Z])/g, ' $1').trim().toLowerCase();
    return `${label} ${normaliseRule(marker.dataset.ttsbRule)} ${marker.dataset[subject]}`;
  }

  /* ------------------------------------------------------------------ *
   * Cart state
   * ------------------------------------------------------------------ */

  function applyCart(cart) {
    if (!cart) return;
    ttsbState = {
      itemCount: cart.item_count || 0,
      totalPrice: cart.total_price || 0,
      lineCount: (cart.items || []).length,
    };
  }

  async function refreshCart() {
    try {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const response = await fetch(`${root}cart.js`.replace(/\/{2,}/g, '/'), {
        cache: 'no-store',
        credentials: 'same-origin',
      });
      if (response.ok) applyCart(await response.json());
    } catch (error) {
      console.warn(`${TT_LOG_PREFIX} could not read the cart`, error);
    }
  }

  async function refreshAndEvaluate(reason) {
    await refreshCart();
    evaluate(reason);
  }

  /* ------------------------------------------------------------------ *
   * Wiring
   * ------------------------------------------------------------------ */

  /**
   * OptiMonk injects its bar after this script runs, so watch for it rather than
   * polling. The observer disconnects as soon as the markers turn up.
   */
  const ttsbWatchedFrames = new WeakSet();

  /**
   * Custom HTML is injected into the iframes AFTER they load, and mutations inside a
   * frame do not reach an observer on the parent document, so each frame needs its own.
   */
  function watchCustomHtmlFrames() {
    document.querySelectorAll(TTSB_CUSTOM_HTML_FRAME).forEach((frame) => {
      if (ttsbWatchedFrames.has(frame)) return;
      ttsbWatchedFrames.add(frame);

      const observeFrame = () => {
        let doc = null;

        try {
          doc = frame.contentDocument;
        } catch (error) {
          return;
        }

        if (!doc || !doc.documentElement) return;

        // Never disconnected. Markers arrive one frame at a time, so stopping after the
        // first successful evaluate meant matching the default and then going deaf to
        // the other pages turning up.
        const observer = new MutationObserver(() => scheduleEvaluate('custom html rendered'));

        observer.observe(doc.documentElement, { childList: true, subtree: true });
        scheduleEvaluate('custom html frame observed');
      };

      frame.addEventListener('load', observeFrame);
      observeFrame();
    });
  }

  /**
   * Watches for the bar and for pages appearing.
   *
   * Deliberately NOT disconnected on first success. OptiMonk injects each page's custom
   * HTML separately, so an early evaluate can see only the default marker, match it,
   * report success, and then miss every page that arrives afterwards - which is exactly
   * what left the bar stuck on the default.
   *
   * Once the campaign container exists we narrow the observer to it, so we are not
   * watching the whole document forever.
   */
  function watchForBar() {
    watchCustomHtmlFrames();
    evaluate('initial');

    ttsbObserver = new MutationObserver(() => {
      watchCustomHtmlFrames();
      scheduleEvaluate('campaign markup changed');
    });

    // Watches the whole document, and watches ATTRIBUTES as well as childList.
    //
    // Two bugs lived here. OptiMonk reveals a campaign by setting the style attribute
    // on its overlay, which is an attribute mutation - with childList alone we never
    // saw it, so an evaluate that ran before the bar appeared matched the default page
    // and nothing re-ran. And narrowing the observer to the first campaign found meant
    // that with several campaigns on the page we went deaf to all the others.
    //
    // Evaluation is debounced and suppressed while our own swap is in flight, so our
    // class and style changes cannot retrigger this.
    ttsbObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class'],
    });
  }

  document.addEventListener(TT_TRAIL_EVENT_NAME, (event) => {
    if (event.detail && typeof event.detail.itemCount === 'number') {
      ttsbState.itemCount = event.detail.itemCount;
      if (event.detail.cart) applyCart(event.detail.cart);
      evaluate('trail added');
    } else {
      refreshAndEvaluate('trail added');
    }
  });

  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) refreshAndEvaluate('tab focused');
  });

  window.ttsbDebug = function ttsbDebug() {
    const markers = findMarkers();

    const report = markers.map((marker) => ({
      rule: describeMarker(marker),
      priority: Number(marker.dataset.ttsbPriority) || 0,
      isDefault: marker.dataset.ttsbDefault !== undefined,
      appliesCoupon: marker.dataset.ttsbApplyCoupon !== undefined,
      matches: markerMatches(marker, ttsbState),
      pageId: (pageFor(marker) || {}).id || '(no page wrapper found)',
    }));

    console.log(`${TT_LOG_PREFIX} cart:`, ttsbState);
    console.table(report);
    return report;
  };

  window.ttsbEvaluate = () => refreshAndEvaluate('manual');

  refreshCart().then(watchForBar);

  /* ======================================================================== *
   * Trail event dispatch (needs pubsub, so it goes last)
   * ======================================================================== */

  if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

  subscribe(PUB_SUB_EVENTS.cartUpdate, async (event) => {
    if (!event) return;

    if (event.cartData && typeof event.cartData.item_count === 'number') {
      applyCart(event.cartData);
      evaluate('cart updated');
    } else {
      await refreshAndEvaluate('cart updated');
    }

    if (event.source !== 'product-form' || !pendingTrail) return;

    const trail = pendingTrail;
    pendingTrail = null;

    const detail = {
      ...trail,
      variantId: event.productVariantId || null,
      addedItem: event.cartData || null,
      itemCount: ttsbState.itemCount,
      cartTotal: ttsbState.totalPrice,
    };

    document.dispatchEvent(new CustomEvent(TT_TRAIL_EVENT_NAME, { bubbles: true, detail }));
  });

  subscribe(PUB_SUB_EVENTS.cartError, (event) => {
    if (!event || event.source !== 'product-form') return;
    pendingTrail = null;
  });
})();
